import { SectionCard } from '@/components/SectionCard/SectionCard'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { CreateAccountDialog } from '@/features/AccountManagement/components/CreateAccountDialog'
import { OrganizationSearchResults } from '@/features/AccountManagement/components/OrganizationSearchResults'
import { UserAccount } from '@/features/AccountManagement/components/UserAccount'
import { useCanCreateAccount } from '@/features/AccountManagement/hooks/useCanCreateAccount'
import {
  accountManagementSearchSchema,
  isAccountTab,
  resolveAccountTab,
  tabSearchParam,
} from '@/features/AccountManagement/lib/accountManagementSearch'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import {
  type SearchUsersError,
  useGetUserDetail,
  useSearchOrganizations,
  useSearchUsers,
} from '@/generated/http-clients/v3ApiComponents'
import type {
  CreateAccountResponse,
  UserSearchResponse,
} from '@/generated/http-clients/v3ApiSchemas'
import { ORG_SEARCH_LIMIT, ORG_SEARCH_MIN_LENGTH } from '@/helpers/orgSearch'
import { applicationRootRoute } from '@/router/rootRoutes'
import {
  Button,
  Code,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  Skeleton,
  Table,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import {
  type UseNavigateResult,
  createRoute,
  useNavigate,
} from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const PATH = 'accounts'
const DEFAULT_PER_PAGE = 25

export const accountManagementRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: PATH,
  component: () => <AccountManagement />,
  validateSearch: accountManagementSearchSchema,
})

export function routeToAccountManagementWithPrefilledEmail(
  navigate: UseNavigateResult<string>,
  email: string,
) {
  navigate({
    to: `/${PATH}`,
    search: {
      q: email,
    },
  })
}

type CreateDialogState =
  | { isOpen: false }
  | { isOpen: true; prefilledEmail?: string }

export function AccountManagement() {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()
  const canCreateAccount = useCanCreateAccount()
  const {
    q: searchParamQuery,
    page: searchParamPage,
    tab,
  } = accountManagementRoute.useSearch()
  const activeTab = resolveAccountTab(tab)

  const [query, setQuery] = React.useState(searchParamQuery || '')
  const [submittedQuery, setSubmittedQuery] = React.useState(
    searchParamQuery || '',
  )
  const [page, setPage] = React.useState(searchParamPage || 0)
  const [expandedUserId, setExpandedUserId] = React.useState<string | null>(
    null,
  )
  const [createDialog, setCreateDialog] = React.useState<CreateDialogState>({
    isOpen: false,
  })
  const [recentlyCreated, setRecentlyCreated] =
    React.useState<CreateAccountResponse | null>(null)

  React.useEffect(() => {
    setQuery(searchParamQuery || '')
    setSubmittedQuery(searchParamQuery || '')
    setPage(searchParamPage || 0)
    setExpandedUserId(null)
  }, [searchParamQuery, searchParamPage])

  const searchQuery = useSearchUsers(
    {
      queryParams: {
        q: submittedQuery,
        page,
        perPage: DEFAULT_PER_PAGE,
      },
    },
    { enabled: activeTab === 'users' && submittedQuery.length >= 1 },
  )

  const orgSearchTerm = submittedQuery.trim()
  const orgSearch = useSearchOrganizations(
    { queryParams: { q: orgSearchTerm, limit: ORG_SEARCH_LIMIT } },
    {
      enabled:
        activeTab === 'orgs' && orgSearchTerm.length >= ORG_SEARCH_MIN_LENGTH,
    },
  )

  const totalPages = searchQuery.data
    ? Math.ceil(searchQuery.data.total / DEFAULT_PER_PAGE)
    : 0

  const handleSearch = () => {
    const trimmed = query.trim()
    if (
      activeTab === 'orgs' &&
      trimmed === orgSearchTerm &&
      trimmed.length >= ORG_SEARCH_MIN_LENGTH
    ) {
      void orgSearch.refetch()
    }
    setSubmittedQuery(trimmed)
    setPage(0)
    setExpandedUserId(null)
    setRecentlyCreated(null)
    navigate({
      to: `/${PATH}`,
      search: {
        tab: tabSearchParam(activeTab),
        q: trimmed || undefined,
        page: undefined,
      },
      replace: true,
    })
  }

  const handleTabChange = (value: string) => {
    if (!isAccountTab(value)) {
      return
    }
    setQuery(searchParamQuery ?? '')
    navigate({
      to: `/${PATH}`,
      search: {
        tab: tabSearchParam(value),
        q: searchParamQuery || undefined,
        page: undefined,
      },
      replace: true,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setExpandedUserId(null)
    navigate({
      to: `/${PATH}`,
      search: { q: submittedQuery || undefined, page: newPage || undefined },
      replace: true,
    })
  }

  const openCreateDialog = () => {
    setRecentlyCreated(null)
    setCreateDialog({ isOpen: true })
  }

  const openCreateDialogWithEmail = () => {
    setRecentlyCreated(null)
    setCreateDialog({ isOpen: true, prefilledEmail: submittedQuery })
  }

  const handleDialogClose = () => {
    setCreateDialog({ isOpen: false })
  }

  const handleAccountCreated = (data: CreateAccountResponse) => {
    setRecentlyCreated(data)
    if (activeTab === 'users') {
      void searchQuery.refetch()
    }
  }

  const dialogPrefilledEmail = createDialog.isOpen
    ? createDialog.prefilledEmail
    : undefined

  const searchPlaceholder =
    activeTab === 'orgs'
      ? t('accountManagement.orgSearch.placeholder')
      : t('accountManagement.search.placeholder')
  const isSearchFetching =
    activeTab === 'orgs' ? orgSearch.isFetching : searchQuery.isFetching

  return (
    <PageLayout
      title={t('accountManagement.pageTitle')}
      maxWidth="8xl"
      action={
        canCreateAccount ? (
          <Button onClick={openCreateDialog} colorPalette="brand">
            {t('accountManagement.createAccount.headerButton')}
          </Button>
        ) : null
      }
    >
      <Tabs.Root
        value={activeTab}
        onValueChange={(details) => handleTabChange(details.value)}
        variant="line"
        lazyMount
        unmountOnExit
        width="100%"
        display="flex"
        flexDirection="column"
        gap={6}
      >
        <Tabs.List>
          <Tabs.Trigger value="users">
            {t('accountManagement.tabs.users')}
          </Tabs.Trigger>
          <Tabs.Trigger value="orgs">
            {t('accountManagement.tabs.orgs')}
          </Tabs.Trigger>
        </Tabs.List>
        <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={4}>
          <GridItem>
            <SectionCard title={t('accountManagement.search.title')}>
              <VStack alignItems="start" gap={4} width="100%">
                <HStack width="100%">
                  <InputGroup
                    flex={1}
                    startElement={<Icon as={Search} size="md" />}
                    startElementProps={{ pointerEvents: 'none' }}
                  >
                    <Input
                      placeholder={String(searchPlaceholder)}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      name="search"
                      type="text"
                    />
                  </InputGroup>
                  <Button
                    onClick={handleSearch}
                    loading={isSearchFetching}
                    colorPalette="blue"
                  >
                    {t('accountManagement.search.button')}
                  </Button>
                </HStack>
              </VStack>
            </SectionCard>
          </GridItem>
        </Grid>
        {recentlyCreated ? (
          <SectionCard
            alertStatus="success"
            alertDescription={
              <HStack gap={2}>
                <Text fontSize="sm">
                  {t('accountManagement.createAccount.banner.success')}
                </Text>
                <Code fontSize="sm">{recentlyCreated.organizationId}</Code>
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: hivesOverviewOrganizationDetailsRoute.to,
                      params: { orgId: recentlyCreated.organizationId },
                    })
                  }
                >
                  {t('accountManagement.createAccount.banner.viewOrgLink')}
                </Button>
              </HStack>
            }
          />
        ) : null}
        <Tabs.Content value="users" pt={0}>
          <PageContent
            searchQuery={searchQuery}
            submittedQuery={submittedQuery}
            expandedUserId={expandedUserId}
            onToggleExpand={(userId) =>
              setExpandedUserId((prev) => (prev === userId ? null : userId))
            }
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            canCreateAccount={canCreateAccount}
            onCreateFromEmptyState={openCreateDialogWithEmail}
          />
        </Tabs.Content>
        <Tabs.Content value="orgs" pt={0}>
          <OrganizationSearchResults
            searchTerm={orgSearchTerm}
            search={orgSearch}
          />
        </Tabs.Content>
      </Tabs.Root>
      <CreateAccountDialog
        isOpen={createDialog.isOpen}
        onClose={handleDialogClose}
        prefilledEmail={dialogPrefilledEmail}
        onCreated={handleAccountCreated}
      />
    </PageLayout>
  )
}

function PageContent({
  searchQuery,
  submittedQuery,
  expandedUserId,
  onToggleExpand,
  page,
  totalPages,
  onPageChange,
  canCreateAccount,
  onCreateFromEmptyState,
}: Readonly<{
  searchQuery: UseQueryResult<UserSearchResponse, SearchUsersError>
  submittedQuery: string
  expandedUserId: string | null
  onToggleExpand: (userId: string) => void
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  canCreateAccount: boolean
  onCreateFromEmptyState: () => void
}>) {
  const { t } = useTranslation('features', { useSuspense: false })

  if (submittedQuery.length < 1) {
    return null
  }

  if (searchQuery.isError) {
    return (
      <SectionCard title={t('accountManagement.error.title')}>
        <Text mt={2} color="red.600">
          {t('accountManagement.error.description')}
        </Text>
      </SectionCard>
    )
  }

  if (searchQuery.isLoading) {
    return <SkeletonLoader />
  }

  if (searchQuery.isSuccess && searchQuery.data.users.length === 0) {
    const isEmailQuery = z.string().email().safeParse(submittedQuery).success
    return (
      <SectionCard>
        <VStack align="start" gap={4}>
          <Text>
            {t('accountManagement.noResults', { query: submittedQuery })}
          </Text>
          {canCreateAccount && isEmailQuery ? (
            <Button onClick={onCreateFromEmptyState} colorPalette="brand">
              {t('accountManagement.createAccount.emptyStateButton', {
                email: submittedQuery,
              })}
            </Button>
          ) : null}
        </VStack>
      </SectionCard>
    )
  }

  if (searchQuery.isSuccess && searchQuery.data.users.length > 0) {
    return (
      <SectionCard>
        <Text fontSize="sm" color="gray.600" mb={4}>
          {t('accountManagement.searchResults.totalResults', {
            total: searchQuery.data.total,
          })}
        </Text>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader width="40px" />
              <Table.ColumnHeader>
                {t('accountManagement.searchResults.email')}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t('accountManagement.searchResults.name')}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t('accountManagement.searchResults.emailVerified')}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {searchQuery.data.users.map((user) => (
              <React.Fragment key={user.id}>
                <Table.Row
                  cursor="pointer"
                  onClick={() => onToggleExpand(user.id)}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Table.Cell>
                    <Icon
                      as={
                        expandedUserId === user.id ? ChevronDown : ChevronRight
                      }
                      size="sm"
                    />
                  </Table.Cell>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>{user.name || '-'}</Table.Cell>
                  <Table.Cell>
                    {user.emailVerified
                      ? t('accountManagement.searchResults.yes')
                      : t('accountManagement.searchResults.no')}
                  </Table.Cell>
                </Table.Row>
                {expandedUserId === user.id && (
                  <Table.Row>
                    <Table.Cell colSpan={4} p={0}>
                      <ExpandedUserDetail userId={user.id} />
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table.Root>
        {totalPages > 1 && (
          <TableFooter
            page={page + 1}
            totalPages={totalPages}
            hasNextPage={page + 1 < totalPages}
            onFirstPage={() => onPageChange(0)}
            onPreviousPage={() => onPageChange(Math.max(0, page - 1))}
            onNextPage={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            onLastPage={() => onPageChange(totalPages - 1)}
          />
        )}
      </SectionCard>
    )
  }
}

function ExpandedUserDetail({ userId }: Readonly<{ userId: string }>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const userQuery = useGetUserDetail({ pathParams: { userId } })

  if (userQuery.isLoading) {
    return (
      <SectionCard>
        <Skeleton h={6} w={40} mb={4} />
        <Skeleton h={6} mb={3} />
        <Skeleton h={6} mb={3} />
        <Skeleton h={6} />
      </SectionCard>
    )
  }

  if (userQuery.isError) {
    return (
      <SectionCard>
        <Text color="red.600">{t('accountManagement.error.description')}</Text>
      </SectionCard>
    )
  }

  if (userQuery.isSuccess && userQuery.data) {
    return <UserAccount user={userQuery.data} />
  }

  return null
}

function SkeletonLoader() {
  return (
    <SectionCard>
      <Skeleton h={8} w={60} mt={1} mb={4} />
      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={8}>
        <GridItem>
          <SectionCard width="100%" height="100%" p={0}>
            <Skeleton h={6} w={40} mb={10} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} />
          </SectionCard>
        </GridItem>
        <GridItem>
          <SectionCard width="100%" height="100%" p={0}>
            <Skeleton h={6} w={40} mb={10} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} />
          </SectionCard>
        </GridItem>
      </Grid>
    </SectionCard>
  )
}
